import { Helmet } from '@dr.pogodin/react-helmet';
import type React from 'react';
import { Route, Routes, useLocation } from 'react-router';
import PageNotFound from '../common/lib/PageNotFound';
import type { BrainAtlasType, MultiLang } from '../config';
import Functions from '../functions';
import { INDEX_ID_PUBLIC } from './lib/IndexUtil';
import styles from './Xoonips.module.css';
import XoonipsAdvancedSearch from './XoonipsAdvancedSearch';
import XoonipsDetailItem from './XoonipsDetailItem';
import XoonipsSearchByAdvancedKeyword from './XoonipsSearchByAdvancedKeyword';
import XoonipsSearchByIndexId from './XoonipsSearchByIndexId';
import XoonipsSearchByItemType from './XoonipsSearchByItemType';
import XoonipsSearchByKeyword from './XoonipsSearchByKeyword';
import XoonipsTop from './XoonipsTop';

interface Props {
    lang: MultiLang;
    type: BrainAtlasType;
}

const ItemDetail: React.FC<Props> = (props) => {
    const { lang, type } = props;
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const itemId_ = params.get('item_id') ?? '';
    const itemId = /^\d+$/.test(itemId_) ? parseInt(itemId_, 10) : 0;
    const doi = params.get('id') ?? '';
    return <XoonipsDetailItem lang={lang} id={itemId} doi={doi} type={type} />;
};

const ItemList: React.FC<Props> = (props) => {
    const { lang, type } = props;
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const id = params.get('index_id') ?? '';
    const indexId = /^\d+$/.test(id) ? parseInt(id, 10) : INDEX_ID_PUBLIC;
    return <XoonipsSearchByIndexId lang={lang} type={type} indexId={indexId} />;
};

const ItemSelect: React.FC<Props> = (props) => {
    const { lang, type } = props;
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const op = params.get('op') ?? '';
    switch (op) {
        case 'itemtypesearch': {
            const searchItemtype = params.get('search_itemtype') ?? '';
            const match = searchItemtype.match(/^xnp([a-z]+)$/);
            const itemType = match !== null ? match[1] : '';
            return <XoonipsSearchByItemType lang={lang} itemType={itemType} subItemType="" type={type} />;
        }
        case 'itemsubtypesearch': {
            const searchItemtype = params.get('search_itemtype') ?? '';
            const match = searchItemtype.match(/^xnp([a-z]+)$/);
            const itemType = match !== null ? match[1] : '';
            const subItemtype = params.get('search_subitemtype') ?? '';
            return <XoonipsSearchByItemType lang={lang} itemType={itemType} subItemType={subItemtype} type={type} />;
        }
        case 'quicksearch': {
            return <XoonipsSearchByKeyword lang={lang} type={type} />;
        }
        case 'advanced': {
            return <XoonipsSearchByAdvancedKeyword lang={lang} type={type} />;
        }
    }
    return <PageNotFound lang={lang} />;
};

const Xoonips: React.FC<Props> = (props) => {
    const { lang, type } = props;
    return (
        <div className={styles.database}>
            <Helmet>
                <title>
                    {Functions.mlang('[en]Database[/en][ja]データベース[/ja]', lang)} - {Functions.siteTitle(lang)}
                </title>
            </Helmet>
            <Routes>
                <Route index element={<XoonipsTop lang={lang} type={type} />} />
                <Route path="index.php" element={<XoonipsTop lang={lang} type={type} />} />
                <Route path="detail.php" element={<ItemDetail lang={lang} type={type} />} />
                <Route path="listitem.php" element={<ItemList lang={lang} type={type} />} />
                <Route path="itemselect.php" element={<ItemSelect lang={lang} type={type} />} />
                <Route path="advanced_search.php" element={<XoonipsAdvancedSearch lang={lang} type={type} />} />
                <Route path="*" element={<PageNotFound lang={lang} />} />
            </Routes>
        </div>
    );
};

export default Xoonips;
